import React, { useState, useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Flame, Check, Circle, Droplets, Heart, ArrowRight, Sparkles, Baby, HeartHandshake } from "lucide-react";
import { ProgressRing, Bar } from "../app/ui";
import { coaches } from "../../data/appdata";
import { isLowPowerClient } from "../../lib/platform";
import StaticGlobe from "./StaticGlobe";
import StoreBadges from "./StoreBadges";

// Desktop-only scroll tilt — mounted only when !lite so phones create no scroll subscription.
function PhoneTilt({ targetRef, children, onHoverStart, onHoverEnd }) {
  const { scrollYProgress } = useScroll({ target: targetRef, offset: ["start end", "end start"] });
  const rotateY = useTransform(scrollYProgress, [0, 0.5, 1], [12, 0, -12]);
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);
  return (
    <motion.div onMouseEnter={onHoverStart} onMouseLeave={onHoverEnd} style={{ rotateY, y, transformPerspective: 1600 }} className="relative">
      {children}
    </motion.div>
  );
}


// ── Mini app screens (styled previews of the real app) ──────────────
function DashboardMini() {
  return (
    <div className="animate-fade-in space-y-3 p-4">
      <div className="text-[10px] uppercase tracking-widest text-brand-soft">Today</div>
      <h4 className="font-display text-lg font-semibold text-mist">Good morning</h4>
      <div className="flex items-center gap-3">
        <ProgressRing value={1840} max={2500} size={92} stroke={8}>
          <span className="font-display text-base font-bold text-mist">1840</span>
        </ProgressRing>
        <div className="flex-1 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-3 text-center">
          <Flame size={22} className="mx-auto text-gold" />
          <div className="font-display text-2xl font-bold text-mist">23</div>
          <div className="text-[9px] uppercase tracking-wider text-mist-muted">day streak</div>
        </div>
      </div>
      {["Log a workout", "Drink 2L water", "Check in"].map((q, i) => (
        <div key={q} className="flex items-center gap-2 rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2">
          {i === 0 ? <Check size={14} className="text-sage" /> : <Circle size={14} className="text-mist-muted" />}
          <span className={`flex-1 text-xs ${i === 0 ? "text-mist-muted line-through" : "text-mist"}`}>{q}</span>
          <span className="text-[10px] text-brand-soft">+{[50, 30, 20][i]}</span>
        </div>
      ))}
    </div>
  );
}

function CoachesMini() {
  return (
    <div className="animate-fade-in space-y-3 p-4">
      <h4 className="font-display text-lg font-semibold text-mist">AI Coaches</h4>
      {coaches.map((c) => (
        <div key={c.id} className="flex items-center gap-3 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-full font-display text-sm font-bold" style={{ background: `${c.accent}22`, color: c.accent }}>{c.name[0]}</span>
          <div><div className="text-sm font-semibold text-mist">{c.name}</div><div className="text-[10px]" style={{ color: c.accent }}>{c.role}</div></div>
        </div>
      ))}
    </div>
  );
}

function NutritionMini() {
  return (
    <div className="animate-fade-in space-y-3 p-4">
      <h4 className="font-display text-lg font-semibold text-mist">Nutrition</h4>
      <div className="flex flex-col items-center">
        <ProgressRing value={1620} max={2100} size={104} stroke={9} color="#D4924A">
          <span className="font-display text-lg font-bold text-mist">1620</span>
          <span className="text-[9px] text-mist-muted">/ 2100</span>
        </ProgressRing>
      </div>
      {[["Protein", 96, 130, "#6AA6FF"], ["Carbs", 180, 260, "#7A9E7E"], ["Fat", 52, 70, "#D4924A"]].map(([l, v, m, c]) => (
        <div key={l}>
          <div className="mb-1 flex justify-between text-[10px]"><span className="text-mist-muted">{l}</span><span className="text-mist">{v}g</span></div>
          <Bar value={v} max={m} color={c} />
        </div>
      ))}
      <div className="flex items-center justify-center gap-2 rounded-xl border border-white/[0.06] bg-white/[0.02] py-2 text-xs text-brand-soft"><Droplets size={13} /> 1.4L water today</div>
    </div>
  );
}

function GlobeMini() {
  return (
    <div className="animate-fade-in">
      <div className="px-4 pt-4"><h4 className="font-display text-lg font-semibold text-mist">Travel Passport</h4></div>
      <StaticGlobe height={340} />
      <div className="px-4 pb-2 text-center text-[10px] text-mist-muted">5 countries stamped · a life in motion</div>
    </div>
  );
}

function FeedMini() {
  return (
    <div className="animate-fade-in space-y-3 p-4">
      <h4 className="font-display text-lg font-semibold text-mist">Activity Feed</h4>
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center gap-2"><span className="text-xl">🇧🇷</span><div><div className="text-xs font-semibold text-mist">Sofia R.</div><div className="text-[9px] text-mist-muted">2h ago · CT</div></div><span className="ml-auto rounded-full bg-brand/15 px-2 py-0.5 text-[9px] text-brand">Run</span></div>
        <div className="mt-3 grid grid-cols-3 gap-2 rounded-xl bg-white/[0.03] p-2 text-center">
          {[["8.2km", "Dist"], ["41:20", "Time"], ["540", "Cal"]].map(([v, l]) => (<div key={l}><div className="font-display text-sm font-bold text-mist">{v}</div><div className="text-[8px] uppercase text-mist-muted">{l}</div></div>))}
        </div>
        <div className="mt-3 flex items-center gap-2 text-xs text-brand"><Heart size={13} fill="currentColor" /> 24</div>
      </div>
    </div>
  );
}

const PREG_ACCENT = "#E7A6C4";
function PregnancyMini() {
  return (
    <div className="animate-fade-in space-y-3 p-4">
      <div className="text-[10px] uppercase tracking-widest" style={{ color: PREG_ACCENT }}>Pregnancy Journey</div>
      <div className="rounded-2xl border p-3 text-center" style={{ borderColor: `${PREG_ACCENT}44`, background: `${PREG_ACCENT}14` }}>
        <Baby size={22} className="mx-auto" style={{ color: PREG_ACCENT }} />
        <div className="mt-1 font-display text-sm font-bold text-mist">Congratulations — you're expecting! 🎉</div>
        <div className="text-[10px] text-mist-muted">WYVN will guide you, week by week</div>
      </div>
      <div className="flex items-center gap-3">
        <ProgressRing value={12} max={40} size={86} stroke={8} color={PREG_ACCENT}>
          <span className="font-display text-sm font-bold text-mist">Wk 12</span>
          <span className="text-[8px] text-mist-muted">of 40</span>
        </ProgressRing>
        <div className="flex-1 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="text-[9px] uppercase tracking-wider text-mist-muted">Baby is about</div>
          <div className="text-sm font-semibold text-mist">the size of a plum</div>
          <div className="mt-1 text-[9px]" style={{ color: PREG_ACCENT }}>First trimester · 196 days to go</div>
        </div>
      </div>
      <div className="rounded-2xl border p-3" style={{ borderColor: `${PREG_ACCENT}33`, background: `${PREG_ACCENT}0D` }}>
        <div className="flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-wider" style={{ color: PREG_ACCENT }}><Sparkles size={11} /> A note from Lumi</div>
        <p className="mt-1 text-[11px] leading-relaxed text-mist">Rest isn't lazy right now — it's how you grow a whole person. Keep water and a snack close today. 💛</p>
      </div>
      <div className="rounded-2xl border border-brand/25 bg-brand/[0.06] p-3">
        <div className="flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-wider text-brand-soft"><HeartHandshake size={11} /> Partner Share</div>
        <p className="mt-1 text-[11px] leading-relaxed text-mist">Your partner sees this too — with caring cues on exactly how to support you this week.</p>
      </div>
    </div>
  );
}

const SCREENS = [
  { key: "dashboard", label: "Your daily home", node: <DashboardMini /> },
  { key: "pregnancy", label: "Pregnancy & partner journey", node: <PregnancyMini /> },
  { key: "coaches", label: "5 AI coaches", node: <CoachesMini /> },
  { key: "nutrition", label: "Track nutrition", node: <NutritionMini /> },
  { key: "globe", label: "A global movement", node: <GlobeMini /> },
  { key: "feed", label: "Move together", node: <FeedMini /> },
];

export default function PhoneShowcase({ onJoin }) {
  const [idx, setIdx] = useState(0);
  const [hover, setHover] = useState(false);
  const secRef = useRef(null);
  const lite = isLowPowerClient();

  useEffect(() => {
    if (hover) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % SCREENS.length), 4200);
    return () => clearInterval(t);
  }, [hover]);

  const phoneInner = (
    <>
      <div className="pointer-events-none absolute -inset-6 rounded-[3rem] bg-brand/15 blur-[50px]" />
      <div className="relative h-[600px] w-[300px] rounded-[2.6rem] border border-white/12 bg-ink-surface p-2.5 shadow-[0_30px_80px_rgba(0,0,0,0.6)]">
        <div className="absolute left-1/2 top-2.5 z-20 h-6 w-28 -translate-x-1/2 rounded-b-2xl bg-black" />
        <div className="relative h-full w-full overflow-hidden rounded-[2.1rem] bg-ink">
          <div key={SCREENS[idx].key} className="h-full overflow-y-auto pt-7">{SCREENS[idx].node}</div>
        </div>
      </div>
    </>
  );

  return (
    <section ref={secRef} id="app" className="relative mx-auto max-w-6xl px-5 py-28 lg:px-8">
      <div className="pointer-events-none absolute left-1/2 top-1/3 h-[460px] w-[820px] -translate-x-1/2 rounded-full bg-brand/10 blur-[150px]" />
      <div className="relative mb-14 text-center">
        <p className="eyebrow">Take a look inside</p>
        <h2 className="mt-5 text-3xl font-semibold text-mist sm:text-5xl">See the app before you download</h2>
        <p className="mx-auto mt-5 max-w-2xl text-mist-muted">Tap through a live preview. This is the calm, intelligent home that lives in your pocket.</p>
      </div>

      <div className="relative grid items-center gap-12 lg:grid-cols-2">
        {/* Phone */}
        <div className="flex justify-center [perspective:1600px]">
          {lite ? (
            <div
              onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
              style={{ transformPerspective: 1600 }}
              className="relative"
            >
              {phoneInner}
            </div>
          ) : (
            <PhoneTilt targetRef={secRef} onHoverStart={() => setHover(true)} onHoverEnd={() => setHover(false)}>
              {phoneInner}
            </PhoneTilt>
          )}
        </div>

        {/* Controls + copy */}
        <div>
          <div className="space-y-2.5">
            {SCREENS.map((s, i) => (
              <button key={s.key} onClick={() => setIdx(i)}
                className={`flex w-full items-center gap-3 rounded-2xl border px-5 py-3.5 text-left transition-all ${idx === i ? "border-brand/40 bg-brand/[0.08]" : "border-white/[0.06] bg-white/[0.02] hover:border-white/15"}`}>
                <span className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold ${idx === i ? "bg-brand text-[#05101f]" : "bg-white/[0.05] text-mist-muted"}`}>{i + 1}</span>
                <span className={`flex-1 text-sm font-medium ${idx === i ? "text-mist" : "text-mist-muted"}`}>{s.label}</span>
                {idx === i && <ArrowRight size={15} className="text-brand" />}
              </button>
            ))}
          </div>

          <div className="mt-8">
            <div className="mb-3 flex items-center gap-2 text-xs font-medium text-brand-soft"><Sparkles size={13} /> Now on Google Play · iOS coming soon</div>
            <StoreBadges className="justify-start" />
            <button onClick={onJoin} className="btn-primary mt-5 inline-flex items-center gap-2">Start free in your browser now <ArrowRight size={16} /></button>
          </div>
        </div>
      </div>
    </section>
  );
}
